import { useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'

import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import RenterAvatar from '../renter-avatar/RenterAvatar'
import { formatBadgeCount } from '../../_utilities/format-badge-count'
import { resolveNavBadgeCount } from '../app-header/helpers'
import {
  Backdrop,
  Panel,
  Header,
  Identity,
  IdentityText,
  IdentityName,
  IdentityLabel,
  CloseButton,
  NavScroll,
  NavRow,
  NavRowBadge,
  Footer,
  ModeSwitchRow,
  SignOutRow,
} from './styled'
import { drawerBackdropMotion, drawerPanelMotion } from './helpers'
import { MobileNavDrawerProps } from './types'

/**
 * Slide-in navigation drawer for narrow viewports. Mirrors the
 * NotificationsDrawer motion + dismissal pattern: opens from the right,
 * closes on ESC, backdrop click, or selecting an entry, and locks body
 * scroll while open. Holds the primary nav, the land switcher, and the
 * sign-out action that the header hides below the `lg` breakpoint.
 *
 * @param {MobileNavDrawerProps} props
 * @returns {JSX.Element | null}
 */
const MobileNavDrawer = ({
  isOpen,
  onClose,
  navEntries,
  activePath,
  badgeCounts,
  mode,
  canSwitch,
  onSwitchMode,
  onSignOut,
}: MobileNavDrawerProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()

  useEffect(() => {
    if (isOpen === false) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen === true) {
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = previousOverflow
      }
    }

    return undefined
  }, [isOpen])

  if (isOpen === false) {
    return null
  }

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  const handleSwitchMode = (): void => {
    onSwitchMode()
    onClose()
  }

  const handleSignOut = (): void => {
    onClose()
    onSignOut()
  }

  const fullName =
    `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
  const displayName = fullName.length > 0 ? fullName : 'Your QuickPass'
  const switchTargetLabel = mode === 'resident' ? 'Search' : 'Resident'

  return (
    <AnimatePresence>
      <Backdrop
        variants={drawerBackdropMotion}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
        <Panel
          variants={drawerPanelMotion}
          initial="hidden"
          animate={{
            ...drawerPanelMotion.visible,
            transition: {
              type: 'tween',
              duration: 0.32,
              ease: [0.32, 0.72, 0, 1],
            },
          }}
          exit="hidden"
          onClick={stopPropagation}
        >
          <Header>
            <Identity>
              <RenterAvatar size="md" />
              <IdentityText>
                <IdentityLabel>Signed in as</IdentityLabel>
                <IdentityName>{displayName}</IdentityName>
              </IdentityText>
            </Identity>
            <CloseButton
              type="button"
              onClick={onClose}
              aria-label="Close menu"
            >
              ×
            </CloseButton>
          </Header>

          <NavScroll>
            {navEntries.map(entry => {
              const badgeCount = resolveNavBadgeCount(entry, badgeCounts)
              return (
                <NavRow
                  key={entry.to}
                  to={entry.to}
                  isActive={activePath === entry.to}
                  onClick={onClose}
                >
                  {entry.label}
                  {badgeCount > 0 ? (
                    <NavRowBadge>{formatBadgeCount(badgeCount)}</NavRowBadge>
                  ) : null}
                </NavRow>
              )
            })}
          </NavScroll>

          <Footer>
            {canSwitch === true ? (
              <ModeSwitchRow type="button" onClick={handleSwitchMode}>
                Switch to {switchTargetLabel} →
              </ModeSwitchRow>
            ) : null}
            <SignOutRow type="button" onClick={handleSignOut}>
              Sign out
            </SignOutRow>
          </Footer>
        </Panel>
      </Backdrop>
    </AnimatePresence>
  )
}

export default MobileNavDrawer
